import { useEffect, useRef, useState } from "react";
import { matchByName } from "./googleMaps";

/**
 * Fills the Company Address fields from a Google place. Country / State / City are dropdowns whose
 * options load one after another, so the state and city are applied once their lists arrive.
 */
export default function useAddressAutofill({
  countries,
  companyStates,
  companyCities,
  selectedCompanyCountryId,
  selectedCompanyStateId,
  setFormData,
  setSelectedCompanyCountryId,
  setSelectedCompanyStateId,
  setCompanyStates,
  setCompanyCities,
}) {
  const pending = useRef(null);
  const [tick, setTick] = useState(0);
  const [note, setNote] = useState("");

  const finish = (missing) => {
    pending.current = null;
    setNote(
      missing.length
        ? `Filled from Google. Please pick ${missing.map((m) => `${m.label} (“${m.value}” is not in the list)`).join(" and ")}.`
        : "Filled from Google. Check the details before saving."
    );
  };

  const onSelect = (addr) => {
    const country = matchByName(countries, addr.country, (c) => c.country_name);
    setFormData((prev) => ({
      ...prev,
      companyAddressLine1: addr.line1 || prev.companyAddressLine1,
      companyAddressLine2: addr.line2,
      companyZipCode: addr.zip || prev.companyZipCode,
      companyCountry: country ? country.country_name : prev.companyCountry,
      companyState: country ? "" : prev.companyState,
      companyCity: country ? "" : prev.companyCity,
    }));
    if (!country) {
      finish(addr.country ? [{ label: "Country", value: addr.country }] : []);
      return;
    }
    pending.current = { stage: "state", state: addr.state, city: addr.city, missing: [] };
    if (country.country_id !== selectedCompanyCountryId) {
      setCompanyStates([]);
      setCompanyCities([]);
      setSelectedCompanyStateId(null);
      setSelectedCompanyCountryId(country.country_id);
    }
    setTick((t) => t + 1);
  };

  useEffect(() => {
    const p = pending.current;
    if (!p || p.stage !== "state" || companyStates.length === 0) return;
    const state = matchByName(companyStates, p.state, (s) => s.state_name);
    if (!state) {
      finish(p.state ? [{ label: "State", value: p.state }, ...(p.city ? [{ label: "City", value: p.city }] : [])] : []);
      return;
    }
    setFormData((prev) => ({ ...prev, companyState: state.state_name, companyCity: "" }));
    p.stage = "city";
    if (state.state_id !== selectedCompanyStateId) {
      setCompanyCities([]);
      setSelectedCompanyStateId(state.state_id);
    } else {
      setTick((t) => t + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyStates, tick]);

  useEffect(() => {
    const p = pending.current;
    if (!p || p.stage !== "city" || companyCities.length === 0) return;
    const city = matchByName(companyCities, p.city, (c) => c.city_name);
    if (city) setFormData((prev) => ({ ...prev, companyCity: city.city_name }));
    finish(city || !p.city ? [] : [{ label: "City", value: p.city }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCities, tick]);

  return { onSelect, note };
}
